/**
 * Kalender: das Abo der Gruppe und der Einzeltermin.
 *
 * Das Abo ist der bequeme Weg — einmal eintragen, danach stehen alle Sitzungen
 * automatisch im Kalender, auch neu angesetzte. Der Einzeltermin ist für alle,
 * die lieber pro Termin entscheiden.
 */
import { useState } from 'react'
import { api, ApiFehler, gespeichertesToken } from '../api/client.js'
import { useLaden } from '../api/laden.js'
import { Amtsknopf, Fehlermeldung } from './Bausteine.js'

interface KalenderAdresse {
  url: string
  webcalUrl: string
}

export function KalenderAbo() {
  const abo = useLaden(() => api.get<KalenderAdresse>('/api/kalender'))
  const [kopiert, setKopiert] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  if (abo.laedt || !abo.daten) return null

  async function kopiere(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url)
      setKopiert(true)
      setTimeout(() => setKopiert(false), 2500)
    } catch {
      setFehler('Das Kopieren ging nicht. Die Adresse steht unten zum Markieren.')
    }
  }

  return (
    <div className="border border-eiche bg-bank px-4 py-4">
      <p className="text-sm text-akte/80">
        Alle Sitzungen der Kammer im eigenen Kalender — einmal abonnieren, danach kommt jeder neue
        Termin von selbst dazu.
      </p>
      <div className="mt-3 flex flex-col gap-2">
        <a
          href={abo.daten.webcalUrl}
          className="bg-messing px-4 py-2.5 text-center font-sans text-sm font-semibold tracking-wide text-saal uppercase transition-colors hover:bg-lampe"
        >
          Im Kalender abonnieren
        </a>
        <Amtsknopf variante="leder" onClick={() => void kopiere(abo.daten?.url ?? '')}>
          {kopiert ? 'Adresse kopiert' : 'Adresse kopieren'}
        </Amtsknopf>
      </div>
      <p className="protokoll mt-2 break-all normal-case">{abo.daten.url}</p>
      <p className="protokoll mt-2 normal-case">
        Die Adresse ist der Schlüssel — wer sie hat, sieht eure Termine. Nicht öffentlich teilen.
      </p>
      <Fehlermeldung text={fehler} />
    </div>
  )
}

/** Ein einzelner Termin zum Herunterladen. */
export function KalenderKnopf({ eventId, titel }: { eventId: number; titel: string }) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  async function hole(): Promise<void> {
    setLaeuft(true)
    setFehler(null)
    try {
      // Der Endpunkt braucht das Token — deshalb holen und als Datei anbieten,
      // statt einfach zu verlinken.
      const antwort = await fetch(`/api/events/${eventId}/kalender.ics`, {
        headers: { authorization: `Bearer ${gespeichertesToken() ?? ''}` },
      })
      if (!antwort.ok) throw new ApiFehler(antwort.status, 'ics', 'Der Termin ließ sich nicht holen.')

      const blob = await antwort.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${titel.replace(/[^\p{L}\p{N}]+/gu, '-').toLowerCase()}.ics`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Der Termin ließ sich nicht holen.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void hole()}
        disabled={laeuft}
        className="protokoll normal-case underline decoration-messing/40 underline-offset-4 hover:text-messing disabled:opacity-50"
      >
        {laeuft ? 'Wird geholt …' : '+ In meinen Kalender'}
      </button>
      <Fehlermeldung text={fehler} />
    </>
  )
}
